import React, { useState } from 'react';
import { 
  FaClipboardList, FaFileAlt, FaMoneyBillWave, FaCheckCircle, 
  FaWhatsapp, FaArrowRight, FaRocket, FaCalendarAlt, FaUserPlus,
  FaBookOpen, FaGraduationCap, FaPhoneAlt, FaEnvelope
} from 'react-icons/fa';
import { GiBookCover } from 'react-icons/gi';
import { MdAssignment, MdVerified } from 'react-icons/md';

const Admissions = () => {
  const steps = [
    { number: "01", title: "Fill Application", icon: FaClipboardList, color: "from-blue-600 to-cyan-500", duration: "10-15 mins", details: "Complete online application form with personal and academic details." },
    { number: "02", title: "Submit Documents", icon: FaFileAlt, color: "from-green-500 to-emerald-500", duration: "5-10 mins", details: "Upload scanned copies of required documents." },
    { number: "03", title: "Pay Fee", icon: FaMoneyBillWave, color: "from-orange-500 to-red-500", duration: "2-3 mins", details: "Pay ₹500 application fee via online banking, card, or UPI." },
    { number: "04", title: "Get Confirmation", icon: FaCheckCircle, color: "from-blue-300 to-blue-400", duration: "24-48 hrs", details: "Receive admission confirmation and enrollment number." }
  ];

  const requirements = [
    { text: "10th & 12th Marksheets (Original & Copies)", icon: GiBookCover },
    { text: "Transfer Certificate (TC) from previous institution", icon: MdAssignment },
    { text: "Character Certificate", icon: MdVerified },
    { text: "Caste Certificate (if applicable)", icon: MdVerified },
    { text: "Passport Size Photographs (4 copies)", icon: FaUserPlus },
    { text: "Aadhar Card Copy", icon: MdVerified },
    { text: "Income Certificate (for scholarship)", icon: FaFileAlt }
  ];

  const dates = [
    { label: "Application Start", date: "April 1, 2026", status: "Open" },
    { label: "Last Date", date: "June 30, 2024", status: "Coming Soon" },
    { label: "Merit List", date: "July 15, 2024", status: "Upcoming" },
    { label: "Counseling", date: "July 20-25, 2024", status: "Upcoming" },
    { label: "Classes Start", date: "August 1, 2024", status: "Upcoming" }
  ];

  const programs = [
    { name: "Bachelor of Arts", seats: 120, duration: "3 Years", fee: "₹15,000/year" },
    { name: "Bachelor of Science", seats: 100, duration: "3 Years", fee: "₹18,000/year" },
    { name: "Bachelor of Commerce", seats: 80, duration: "3 Years", fee: "₹16,000/year" },
    { name: "Master of Arts", seats: 60, duration: "2 Years", fee: "₹20,000/year" },
    { name: "Master of Science", seats: 50, duration: "2 Years", fee: "₹22,000/year" },
    { name: "Computer Applications (BCA)", seats: 90, duration: "3 Years", fee: "₹25,000/year" }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-blue-50/30">
      <div className="bg-gradient-to-r from-blue-700 to-indigo-700 py-20 text-center text-white">
        <h1 className="text-4xl md:text-5xl font-bold mb-4">Admissions 2024-25</h1>
        <div className="w-20 h-1 bg-yellow-400 mx-auto rounded-full mb-4"></div>
        <p className="text-lg max-w-2xl mx-auto">Begin your journey with us. Simple, transparent admission process.</p>
      </div>

      <div className="container mx-auto px-6 py-16">
        {/* Steps */}
        <div className="grid md:grid-cols-4 gap-6 mb-16">
          {steps.map((step, idx) => (
            <div key={idx} className="bg-white rounded-xl p-6 shadow-md hover:shadow-xl transition-all hover:-translate-y-1">
              <div className={`w-12 h-12 bg-gradient-to-r ${step.color} rounded-xl flex items-center justify-center mb-4`}>
                <step.icon className="text-white text-xl" />
              </div>
              <div className="text-3xl font-bold text-gray-200 mb-2">{step.number}</div>
              <h3 className="text-lg font-bold mb-2">{step.title}</h3>
              <p className="text-sm text-gray-500">{step.duration}</p>
              <p className="text-xs text-gray-400 mt-2">{step.details}</p>
            </div>
          ))}
        </div>

        {/* Programs Table */}
        <div className="mb-16">
          <h3 className="text-2xl font-bold text-gray-800 mb-6 text-center">Programs Offered</h3>
          <div className="bg-white rounded-2xl shadow-md overflow-hidden">
            <div className="hidden md:grid md:grid-cols-4 bg-gray-50 px-6 py-3 border-b font-semibold text-gray-700">
              <div>Program</div><div>Duration</div><div>Seats</div><div>Annual Fee</div>
            </div>
            {programs.map((program, idx) => (
              <div key={idx} className="grid md:grid-cols-4 gap-3 p-4 border-b hover:bg-gray-50 transition">
                <div className="font-semibold text-gray-800">{program.name}</div>
                <div className="text-gray-600">{program.duration}</div>
                <div className="text-gray-600">{program.seats}</div>
                <div className="text-gray-600">{program.fee}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Requirements & Dates */}
        <div className="grid md:grid-cols-2 gap-8 mb-12">
          <div className="bg-white rounded-2xl p-6 shadow-md">
            <h3 className="text-xl font-bold mb-4">Required Documents</h3>
            <ul className="space-y-2">
              {requirements.map((req, idx) => (
                <li key={idx} className="flex items-center gap-2 text-gray-600 text-sm">
                  <req.icon className="text-blue-600 text-xs" /> {req.text}
                </li>
              ))}
            </ul>
            <p className="text-xs text-gray-400 mt-3">*Self-attested copies required</p>
          </div>
          <div className="bg-white rounded-2xl p-6 shadow-md">
            <h3 className="text-xl font-bold mb-4">Important Dates</h3>
            <div className="space-y-3">
              {dates.map((item, idx) => (
                <div key={idx} className="flex justify-between items-center pb-2 border-b border-gray-100">
                  <div>
                    <p className="font-medium">{item.label}</p>
                    <p className="text-xs text-gray-500">{item.date}</p>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-semibold ${item.status === 'Open' ? 'bg-green-100 text-green-600' : 'bg-yellow-100 text-yellow-600'}`}>
                    {item.status}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Fee Structure */}
        <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-6 mb-12">
          <h3 className="text-xl font-bold text-gray-800 mb-3">Fee Structure (Approximate)</h3>
          <div className="grid md:grid-cols-3 gap-4">
            <div><span className="font-semibold">Application Fee:</span> ₹500 (non-refundable)</div>
            <div><span className="font-semibold">Tuition Fee:</span> ₹15,000 - ₹25,000 per year</div>
            <div><span className="font-semibold">Other Charges:</span> Library, lab, sports, etc. (as applicable)</div>
          </div>
          <p className="text-xs text-gray-500 mt-3">*Scholarships available for meritorious and economically weaker students</p>
        </div>

        {/* CTA */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl p-8 text-center text-white">
          <h3 className="text-2xl font-bold mb-2">Ready to Apply?</h3>
          <p className="mb-4">Limited seats available. Apply now for the academic year 2024-25</p>
          <div className="flex gap-3 justify-center">
            <button className="bg-white text-blue-600 px-6 py-2 rounded-full font-semibold hover:shadow-lg transition">Apply Now</button>
            <button className="bg-transparent border-2 border-white text-white px-6 py-2 rounded-full font-semibold hover:bg-white hover:text-blue-600 transition flex items-center gap-2">
              <FaWhatsapp /> Chat on WhatsApp
            </button>
          </div>
          <div className="mt-4 text-sm flex justify-center gap-4">
            <span className="flex items-center gap-1"><FaPhoneAlt /> +91-9984-485-915</span>
            <span className="flex items-center gap-1"><FaEnvelope /> admissions@rdsgroupofcolleges.com</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Admissions;